/**
 * Comprehensive Notification Service
 * 
 * Handles email, push, and in-app notifications for the McKenzie Portal
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  setDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  BaseNotification, 
  PushNotification, 
  EmailNotification, 
  InAppNotification,
  NotificationType,
  NotificationPriority,
  NotificationChannel,
  NotificationStatus,
  UserNotificationPreferences,
  FCMToken,
  NotificationDeliveryResult
} from '../@types/notification';
import { EmailService } from './EmailService';

export class NotificationService {
  private static instance: NotificationService;
  private fcmTokens: Map<string, FCMToken> = new Map();

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Send a notification through multiple channels
   */
  async sendNotification(notification: BaseNotification): Promise<NotificationDeliveryResult[]> {
    const results: NotificationDeliveryResult[] = [];

    try {
      // Save notification to database
      const notificationRef = await addDoc(collection(db, 'Notifications'), {
        ...notification,
        createdAt: Timestamp.now(),
        status: 'pending' as NotificationStatus
      });

      // Send through each channel
      for (const channel of notification.channels) {
        try {
          let result: NotificationDeliveryResult;
          
          switch (channel) {
            case 'email':
              result = await this.sendEmailNotification(notification as EmailNotification);
              break;
            case 'push':
              result = await this.sendPushNotification(notification as PushNotification);
              break;
            case 'in_app':
              result = await this.sendInAppNotification(notification as InAppNotification);
              break;
            default:
              result = {
                notificationId: notificationRef.id,
                channel,
                status: 'failed',
                error: `Unsupported channel: ${channel}`
              };
          }

          results.push(result);
          
          // Update notification status
          await updateDoc(notificationRef, {
            [`deliveryResults.${channel}`]: result,
            status: result.status === 'success' ? 'sent' : 'failed'
          });

        } catch (error: any) {
          const failedResult: NotificationDeliveryResult = {
            notificationId: notificationRef.id,
            channel,
            status: 'failed',
            error: error.message
          };
          results.push(failedResult);
        }
      }

      return results;

    } catch (error: any) {
      console.error('Failed to send notification:', error);
      throw new Error(`Failed to send notification: ${error.message}`);
    }
  }

  /**
   * Send email notification
   */
  private async sendEmailNotification(notification: EmailNotification): Promise<NotificationDeliveryResult> {
    try {
      // Check user preferences
      if (notification.userId) {
        const preferences = await this.getUserPreferences(notification.userId);
        if (!preferences?.email.enabled) {
          return {
            notificationId: notification.id,
            channel: 'email',
            status: 'failed',
            error: 'Email notifications disabled by user'
          };
        }
      }

      await EmailService.sendEmail({
        to: [notification.userId || 'manager@mckenzie.com'],
        subject: notification.subject,
        body: notification.htmlContent || notification.message,
        attachments: notification.attachments?.map(att => ({
          filename: att.filename,
          content: new Blob([att.content], { type: att.contentType }),
          contentType: att.contentType
        }))
      });

      return {
        notificationId: notification.id,
        channel: 'email',
        status: 'success',
        deliveredAt: Timestamp.now()
      };

    } catch (error: any) {
      return {
        notificationId: notification.id,
        channel: 'email',
        status: 'failed',
        error: error.message
      };
    }
  }

  /**
   * Send push notification via FCM
   */
  private async sendPushNotification(notification: PushNotification): Promise<NotificationDeliveryResult> {
    try {
      if (!notification.userId) {
        return {
          notificationId: notification.id,
          channel: 'push',
          status: 'failed',
          error: 'User ID required for push notifications'
        };
      }

      // Check user preferences
      const preferences = await this.getUserPreferences(notification.userId);
      if (!preferences?.push.enabled) {
        return {
          notificationId: notification.id,
          channel: 'push',
          status: 'failed',
          error: 'Push notifications disabled by user'
        };
      }

      // Get FCM token
      const fcmToken = await this.getFCMToken(notification.userId);
      if (!fcmToken) {
        return {
          notificationId: notification.id,
          channel: 'push',
          status: 'failed',
          error: 'No FCM token found for user'
        };
      }

      // Send via FCM (implementation would use Firebase Admin SDK)
      const message = {
        token: fcmToken.token,
        notification: {
          title: notification.title,
          body: notification.message
        },
        data: notification.data || {}
      };

      // For now, simulate FCM sending
      console.log('📱 Sending push notification:', message);
      
      // In production, this would use Firebase Admin SDK:
      // await admin.messaging().send(message);

      return {
        notificationId: notification.id,
        channel: 'push',
        status: 'success',
        messageId: `fcm_${Date.now()}`,
        deliveredAt: Timestamp.now()
      };

    } catch (error: any) {
      return {
        notificationId: notification.id,
        channel: 'push',
        status: 'failed',
        error: error.message
      };
    }
  }

  /**
   * Send in-app notification
   */
  private async sendInAppNotification(notification: InAppNotification): Promise<NotificationDeliveryResult> {
    try {
      // Save to user's notification center
      await addDoc(collection(db, 'users', notification.userId, 'notifications'), {
        userId: notification.userId,
        notificationId: notification.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        priority: notification.priority,
        icon: notification.icon,
        color: notification.color,
        actionButtons: notification.actionButtons,
        isRead: false,
        createdAt: Timestamp.now()
      });

      return {
        notificationId: notification.id,
        channel: 'in_app',
        status: 'success',
        deliveredAt: Timestamp.now()
      };

    } catch (error: any) {
      return {
        notificationId: notification.id,
        channel: 'in_app',
        status: 'failed',
        error: error.message
      };
    }
  }

  /**
   * Get user notification preferences
   */
  async getUserPreferences(userId: string): Promise<UserNotificationPreferences | null> {
    try {
      const docRef = doc(db, 'UserPreferences', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as UserNotificationPreferences;
      }
      
      // Return default preferences
      return this.getDefaultPreferences(userId);
    } catch (error) {
      console.error('Failed to get user preferences:', error);
      return this.getDefaultPreferences(userId);
    }
  }

  /**
   * Update user notification preferences
   */
  async updateUserPreferences(userId: string, preferences: Partial<UserNotificationPreferences>): Promise<void> {
    try {
      const docRef = doc(db, 'UserPreferences', userId);
      await updateDoc(docRef, {
        ...preferences,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Failed to update user preferences:', error);
      throw error;
    }
  }

  /**
   * Get default notification preferences
   */
  private getDefaultPreferences(userId: string): UserNotificationPreferences {
    return {
      userId,
      email: {
        enabled: true,
        orderUpdates: true,
        systemAlerts: true,
        weeklyReports: true,
        securityAlerts: true,
        marketingEmails: false,
        digestFrequency: 'weekly'
      },
      push: {
        enabled: true,
        orderUpdates: true,
        systemAlerts: true,
        reminders: true,
        achievements: true,
        quietHours: {
          enabled: false,
          startTime: '22:00',
          endTime: '08:00',
          timezone: 'America/Toronto'
        }
      },
      sms: {
        enabled: false,
        urgentAlerts: true,
        securityAlerts: true,
        twoFactorCodes: true
      },
      inApp: {
        enabled: true,
        showBanners: true,
        showInNotificationCenter: true,
        autoMarkAsRead: false,
        retentionDays: 30
      },
      global: {
        language: 'en',
        timezone: 'America/Toronto',
        digestEnabled: true,
        digestTime: '09:00'
      },
      updatedAt: Timestamp.now()
    };
  }

  /**
   * Register FCM token for user
   * Updates existing token if it exists, otherwise creates a new one
   */
  async registerFCMToken(userId: string, token: string, platform: 'web' | 'android' | 'ios'): Promise<void> {
    try {
      // Check if token already exists for this user/platform combination
      const userTokensRef = collection(db, 'users', userId, 'fcmTokens');
      const existingTokenQuery = query(
        userTokensRef,
        where('platform', '==', platform),
        where('isActive', '==', true),
        limit(1)
      );

      const existingTokens = await getDocs(existingTokenQuery);
      
      const tokenData = {
        userId,
        token,
        platform,
        browserName: platform === 'web' ? this.getBrowserName() : undefined,
        browserVersion: platform === 'web' ? this.getBrowserVersion() : undefined,
        isActive: true,
        updatedAt: Timestamp.now(),
        lastUsed: Timestamp.now()
      };

      if (!existingTokens.empty) {
        // Update existing token
        const existingTokenDoc = existingTokens.docs[0];
        const existingTokenData = existingTokenDoc.data();
        
        // Only update if token has changed
        if (existingTokenData.token !== token) {
          await updateDoc(existingTokenDoc.ref, {
            token,
            browserName: tokenData.browserName,
            browserVersion: tokenData.browserVersion,
            updatedAt: tokenData.updatedAt,
            lastUsed: tokenData.lastUsed
          });
        } else {
          // Just update lastUsed timestamp
          await updateDoc(existingTokenDoc.ref, {
            lastUsed: tokenData.lastUsed,
            updatedAt: tokenData.updatedAt
          });
        }
        
        // Update cache
        this.fcmTokens.set(userId, {
          ...existingTokenData,
          ...tokenData,
          createdAt: existingTokenData.createdAt || Timestamp.now()
        } as FCMToken);
      } else {
        // Create new token document
        const newTokenData = {
          ...tokenData,
          createdAt: Timestamp.now()
        };
        
        // Use setDoc with a generated ID to avoid conflicts
        const tokenRef = doc(collection(db, 'users', userId, 'fcmTokens'));
        await setDoc(tokenRef, newTokenData);
        
        // Update cache
        this.fcmTokens.set(userId, {
          id: tokenRef.id,
          ...newTokenData
        } as FCMToken);
      }
    } catch (error: any) {
      console.error('Failed to register FCM token:', error);
      // Don't throw - allow app to continue if token registration fails
      // This prevents blocking the login process
    }
  }

  /**
   * Get FCM token for user
   */
  private async getFCMToken(userId: string): Promise<FCMToken | null> {
    try {
      if (this.fcmTokens.has(userId)) {
        return this.fcmTokens.get(userId)!;
      }

      const q = query(
        collection(db, 'users', userId, 'fcmTokens'),
        where('isActive', '==', true),
        orderBy('updatedAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const tokenData = querySnapshot.docs[0].data() as FCMToken;
        this.fcmTokens.set(userId, tokenData);
        return tokenData;
      }

      return null;
    } catch (error) {
      console.error('Failed to get FCM token:', error);
      return null;
    }
  }

  /**
   * Get browser name
   */
  private getBrowserName(): string {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Get browser version
   */
  private getBrowserVersion(): string {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
    return match ? match[2] : 'Unknown';
  }

  /**
   * Create order notification
   */
  async createOrderNotification(
    type: 'order_created' | 'order_updated' | 'order_approved' | 'order_rejected' | 'order_completed',
    orderId: string,
    storeName: string,
    userId?: string
  ): Promise<void> {
    const notifications = {
      order_created: {
        title: 'New Order Created',
        message: `A new order has been created for ${storeName}`,
        priority: 'normal' as NotificationPriority
      },
      order_updated: {
        title: 'Order Updated',
        message: `Order for ${storeName} has been updated`,
        priority: 'normal' as NotificationPriority
      },
      order_approved: {
        title: 'Order Approved',
        message: `Your order for ${storeName} has been approved`,
        priority: 'high' as NotificationPriority
      },
      order_rejected: {
        title: 'Order Rejected',
        message: `Your order for ${storeName} has been rejected`,
        priority: 'high' as NotificationPriority
      },
      order_completed: {
        title: 'Order Completed',
        message: `Order for ${storeName} has been completed`,
        priority: 'normal' as NotificationPriority
      }
    };

    const notificationData = notifications[type];

    const notification: BaseNotification = {
      id: `order_${type}_${orderId}_${Date.now()}`,
      type,
      title: notificationData.title,
      message: notificationData.message,
      priority: notificationData.priority,
      channels: userId ? ['email', 'push', 'in_app'] : ['email'],
      status: 'pending',
      userId,
      metadata: {
        orderId,
        storeName,
        entityType: 'order'
      },
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
    };

    await this.sendNotification(notification);
  }

  /**
   * Create system alert notification
   */
  async createSystemAlert(
    title: string,
    message: string,
    priority: NotificationPriority = 'normal',
    userId?: string
  ): Promise<void> {
    const notification: BaseNotification = {
      id: `system_alert_${Date.now()}`,
      type: 'system_alert',
      title,
      message,
      priority,
      channels: userId ? ['email', 'push', 'in_app'] : ['email'],
      status: 'pending',
      userId,
      createdAt: new Date(),
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
    };

    await this.sendNotification(notification);
  }
}
